/* Batch 34 · Z-16 — "Link to an existing misstatement" next to "Send to Misstatements" on a worksheet row (unrecorded
   liabilities 3203-07 routes 1 and 2, cut-off rows and post-year-end rows): pick a W9 row of the same engagement; the row counts
   as sent and no second misstatement is created (POST …/misstatements/link). "Unlink" (POST …/misstatements/unlink) until the
   worksheet is signed — the server refuses after that, with its own EN / AR text. */
import React, { useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

const NF = { format: (n) => fmtNumber(n) };   // STEP 3 · rule 2 · B-2e
const errText = (e, lang) => (lang === "ar" && e?.response?.data?.detail_ar) || toErrMsg(e);

/** body: {target, source_kind?, source_id?} — the unrecorded-liabilities targets need no source ids (the server derives them). */
export function LinkExistingButton({ engId, body, onDone, disabled, testid }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const openPicker = async () => {
    setOpen(true); setRows(null);
    try {
      const r = await api.get(`/engagements/${engId}/w9`, { _silent: true });
      const d = r.data || {};
      setRows([...(d.rows_unresolved || []), ...(d.rows_resolved || [])]);
    } catch (e) { setRows([]); toast.error(errText(e, lang)); }
  };
  const pick = async (id) => {
    setBusy(true);
    try {
      await api.post(`/engagements/${engId}/misstatements/link`, { ...body, misstatement_id: id });
      toast.success(t("b34d.linked")); setOpen(false);
      if (onDone) await onDone();
    } catch (e) { toast.error(errText(e, lang)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" disabled={disabled} className="rounded border px-1 text-[11px] text-sky-700" data-testid={testid}
              onClick={openPicker}>{t("b34d.linkExisting")}</button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid={`${testid}-dialog`}>
          <div className="max-h-[80vh] w-[640px] max-w-[95vw] overflow-auto rounded bg-white p-4 text-xs shadow dark:bg-slate-900">
            <div className="mb-1 font-medium">{t("b34d.linkTitle")}</div>
            <p className="mb-2 text-muted-foreground">{t("b34d.linkIntro")}</p>
            {rows === null ? <div>…</div> : rows.length === 0 ? <div data-testid={`${testid}-none`}>{t("b34d.linkNone")}</div> : (
              <table className="w-full"><tbody>
                {rows.map((m) => (
                  <tr key={m.id} className="border-t" data-testid={`${testid}-opt-${m.id}`}>
                    <td className="py-1 pe-2 font-mono">{m.fs_head || "—"}</td>
                    <td className="pe-2">{m.description}</td>
                    <td className="pe-2 text-end tabular-nums" dir="ltr">{NF.format(Number(m.amount) || 0)}</td>
                    <td><button type="button" disabled={busy} className="rounded border border-sky-600 px-1 text-sky-700"
                                data-testid={`${testid}-pick-${m.id}`} onClick={() => pick(m.id)}>{t("b34d.linkPick")}</button></td>
                  </tr>))}
              </tbody></table>)}
            <div className="mt-3 text-end">
              <button type="button" className="rounded border px-2 py-1" onClick={() => setOpen(false)}>{t("b34d.cancel")}</button>
            </div>
          </div>
        </div>)}
    </>
  );
}

export function UnlinkButton({ engId, body, onDone, disabled, testid }) {
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/engagements/${engId}/misstatements/unlink`, body);
      toast.success(t("b34d.unlinked"));
      if (onDone) await onDone();
    } catch (e) { toast.error(errText(e, lang)); } finally { setBusy(false); }
  };
  return (
    <button type="button" disabled={disabled || busy} className="text-[11px] underline" data-testid={testid} onClick={go}>
      {t("b34d.unlink")}</button>
  );
}
